import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { Copy, ExternalLink } from 'lucide-react';
import { FormGroup, Label, Input as ReactstrapInput } from 'reactstrap';

import Input from 'src/componentes/input';
import Button from 'src/componentes/button';
import { Switch } from 'src/componentes/Form';
import Info from 'src/componentes/info';
import Panel from 'src/componentes/panel';
import useAuth from 'src/hooks/useAuth';
import { TIPO_IDENTIFICACAO_FORMULARIO_GLOBAL } from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils/string';
import {
  copyToClipboard,
  FORMATO_EXIBICAO_FORMULARIO_GLOBAL,
  getLookupId,
} from 'src/paginas/avaliacao-desempenho/PesquisaClima/utils';
import AplicacaoPesquisaClimaSection from './AplicacaoPesquisaClimaSection';

const styles = {
  textarea: {
    minHeight: 95,
    resize: 'vertical',
  },
  optionRow: {
    alignItems: 'center',
    borderBottom: '1px solid #e9ecef',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '18px 0',
  },
  optionRowLast: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: 18,
  },
  tipoPesquisaRow: {
    alignItems: 'center',
    borderBottom: '1px solid #e9ecef',
    display: 'flex',
    gap: 16,
    padding: '18px 0',
  },
  optionTitle: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 14,
    gap: 4,
    lineHeight: '20px',
    marginBottom: 2,
  },
  optionDescription: {
    color: '#6c757d',
    fontSize: 13,
    lineHeight: '18px',
  },
  numberInput: {
    maxWidth: 96,
  },
  resultControls: {
    alignItems: 'center',
    display: 'flex',
    gap: 12,
  },
  acessoSection: {
    marginTop: 24,
  },
  acessoTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 12,
  },
  acessoBox: {
    backgroundColor: '#f5f6f8',
    borderRadius: 8,
    padding: 16,
  },
  acessoLabel: {
    color: '#6c757d',
    fontSize: 13,
    marginBottom: 8,
  },
  acessoRow: {
    alignItems: 'center',
    display: 'flex',
    gap: 12,
    justifyContent: 'space-between',
  },
  acessoLink: {
    color: '#1c7ed6',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  acessoActions: {
    display: 'flex',
    flexShrink: 0,
    gap: 12,
  },
};

function AcessoPesquisaSection({ link }) {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  if (!link) return null;

  const handleCopiar = async () => {
    await copyToClipboard(link);
    enqueueSnackbar(
      intl.formatMessage({ id: 'labelLinkPesquisaCopiadoComSucesso' }),
      { variant: 'success' }
    );
  };

  return (
    <div style={styles.acessoSection}>
      <div style={styles.acessoBox}>
        <div style={styles.acessoTitle}>
          {intl.formatMessage({ id: 'labelLinkParaPesquisa' })}
        </div>
        <div style={styles.acessoRow}>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            title={link}
            style={styles.acessoLink}
          >
            {link}
          </a>
          <div style={styles.acessoActions}>
            <Button
              color="outline-primary"
              leftIcon={Copy}
              onClick={handleCopiar}
            >
              {intl.formatMessage({ id: 'labelCopiarLink' })}
            </Button>
            <Button
              color="outline-primary"
              leftIcon={ExternalLink}
              onClick={() => window.open(link, '_blank')}
            >
              {intl.formatMessage({ id: 'labelAbrirLink' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BooleanSwitch({ defaultValue = false, id, model, disabled }) {
  const checked = model.value ?? defaultValue;

  return (
    <Switch
      id={id}
      name={id}
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => model.requestChange(e.target.checked)}
    />
  );
}

function ConfiguracaoSwitch({
  defaultValue,
  id,
  title,
  description,
  tooltip,
  model,
  disabled,
}) {
  return (
    <div style={styles.optionRow}>
      <div>
        <div style={styles.optionTitle}>
          {title}
          {tooltip && (
            <Info
              id={`${id}-info`}
              placement="right"
              text={tooltip}
              tooltipMaxWidth={320}
              tooltipTextAlign="left"
            />
          )}
        </div>
        {description && (
          <div style={styles.optionDescription}>{description}</div>
        )}
      </div>
      <BooleanSwitch
        defaultValue={defaultValue}
        id={id}
        model={model}
        disabled={disabled}
      />
    </div>
  );
}

const QUANTIDADE_MINIMA_PADRAO = 2;

function NumberField({ label, description, model, disabled }) {
  const value = model.value ?? QUANTIDADE_MINIMA_PADRAO;

  useEffect(() => {
    if (model.value == null && !disabled) {
      model.requestChange(QUANTIDADE_MINIMA_PADRAO);
    }
  }, [model, disabled]);

  const handleChange = (e) => {
    const nextValue = e.target.value;
    model.requestChange(nextValue === '' ? null : Number(nextValue));
  };

  return (
    <div style={styles.optionRowLast}>
      <FormGroup className="mb-0">
        <Label className="mb-1">{label}</Label>
        <div style={styles.optionDescription}>{description}</div>
      </FormGroup>
      <div style={styles.resultControls}>
        <ReactstrapInput
          min={0}
          step={1}
          type="number"
          value={value}
          disabled={disabled}
          onChange={handleChange}
          style={styles.numberInput}
        />
      </div>
    </div>
  );
}

function EnvioPesquisaClimaTab({
  disabled,
  disabledDatas = disabled,
  linkPesquisa,
  model,
  previewFocusTrapPriority,
  showRequiredErrors,
}) {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const models = {
    dataInicio: model.getModel('dataInicio'),
    dataTermino: model.getModel('dataTermino'),
    mensagemConvite: model.getModel('mensagemConvite'),
    mensagemFinal: model.getModel('mensagemFinal'),
    permitirApenasUmaRespostaPorParticipante: model.getModel(
      'permitirApenasUmaRespostaPorParticipante'
    ),
    desconsiderarColaboradoresInativos: model.getModel(
      'desconsiderarColaboradoresInativos'
    ),
    randomizarPerguntas: model.getModel('randomizarPerguntas'),
    formatoExibicao: model.getModel('formatoExibicao'),
    exibirBarraProgresso: model.getModel('exibirBarraProgresso'),
    /*permitirRemoverConfidencialidade: model.getModel(
      'permitirRemoverConfidencialidade'
    ),*/
    quantidadeMinimaRespostasParaExibirResultado: model.getModel(
      'quantidadeMinimaRespostasParaExibirResultado'
    ),
    tipoIdentificacao: model.getModel('tipoIdentificacao'),
  };

  const tipoPesquisaOptions = [
    {
      id: TIPO_IDENTIFICACAO_FORMULARIO_GLOBAL.CONFIDENCIAL,
      nome: intl.formatMessage({ id: 'labelConfidencial' }),
    },
    {
      id: TIPO_IDENTIFICACAO_FORMULARIO_GLOBAL.IDENTIFICADA,
      nome: intl.formatMessage({ id: 'labelIdentificada' }),
    },
  ];
  const formatoExibicaoId = getLookupId(models.formatoExibicao.value);
  const tipoIdentificacaoId = getLookupId(models.tipoIdentificacao.value);
  const exibirUmaPerguntaPorVezAtivo =
    formatoExibicaoId === FORMATO_EXIBICAO_FORMULARIO_GLOBAL.PERGUNTA_POR_VEZ;
  const formatoExibicaoPerguntaPorVezModel = {
    value: exibirUmaPerguntaPorVezAtivo,
    requestChange: (checked) => {
      models.formatoExibicao.requestChange(
        checked
          ? FORMATO_EXIBICAO_FORMULARIO_GLOBAL.PERGUNTA_POR_VEZ
          : FORMATO_EXIBICAO_FORMULARIO_GLOBAL.PAGINA_UNICA
      );

      if (!checked) {
        models.exibirBarraProgresso.requestChange(false);
      }
    },
  };
  const exibirBarraProgressoModel = {
    value: exibirUmaPerguntaPorVezAtivo
      ? models.exibirBarraProgresso.value
      : false,
    requestChange: models.exibirBarraProgresso.requestChange,
  };

  return (
    <>
      <AcessoPesquisaSection link={linkPesquisa} />
      <div style={{ marginTop: 24, marginBottom: 24 }} />
      <Panel
        header={intl.formatMessage({ id: 'labelConfiguracoesEnvio' })}
        style={{ marginBottom: 0 }}
      >
        <Panel
          header={capitalizeFirstLetter(
            intl.formatMessage({ id: 'label.informacoes' })
          )}
        >
          <div style={styles.tipoPesquisaRow}>
            <Label
              className={`mb-0 ${
                models.tipoIdentificacao.errors ? 'tw:text-red-600' : ''
              }`}
            >
              {intl.formatMessage({ id: 'labelTipoPesquisa' })} *
            </Label>
            {tipoPesquisaOptions.map((opt) => (
              <FormGroup check inline className="mb-0" key={opt.id}>
                <ReactstrapInput
                  type="radio"
                  name="tipoIdentificacao"
                  id={`tipo-pesquisa-${opt.id}`}
                  checked={tipoIdentificacaoId === opt.id}
                  invalid={!!models.tipoIdentificacao.errors}
                  aria-invalid={!!models.tipoIdentificacao.errors}
                  disabled={disabled}
                  onChange={() =>
                    models.tipoIdentificacao.requestChange(opt.id)
                  }
                  style={{ marginTop: 3 }}
                />
                <Label check for={`tipo-pesquisa-${opt.id}`} className="mb-0">
                  {opt.nome}
                </Label>
              </FormGroup>
            ))}
          </div>
          {models.tipoIdentificacao.errors && (
            <p className="tw:mt-2 tw:text-sm tw:text-red-600" role="alert">
              {models.tipoIdentificacao.errors}
            </p>
          )}
          <Input
            type="textarea"
            rows={4}
            placeholder={intl.formatMessage({
              id: 'labelPlaceholderMensagemConvitePesquisaClima',
            })}
            model={{
              label: intl.formatMessage({ id: 'labelMensagemConvite' }),
              value: models.mensagemConvite.value,
              requestChange: models.mensagemConvite.requestChange,
              errors: models.mensagemConvite.errors,
            }}
            disabled={disabled}
            style={styles.textarea}
          />
          <Input
            type="textarea"
            rows={4}
            placeholder={intl.formatMessage({
              id: 'labelPlaceholderMensagemFinalPesquisaClima',
            })}
            model={{
              label: intl.formatMessage({ id: 'labelMensagemFinal' }),
              value: models.mensagemFinal.value,
              requestChange: models.mensagemFinal.requestChange,
              errors: models.mensagemFinal.errors,
            }}
            disabled={disabled}
            style={styles.textarea}
          />
        </Panel>
        <AplicacaoPesquisaClimaSection
          dataInicioModel={models.dataInicio}
          dataTerminoModel={models.dataTermino}
          disabled={disabledDatas}
          model={model.getModel('aplicacao')}
          pesquisaModel={model}
          previewFocusTrapPriority={previewFocusTrapPriority}
          showRequiredErrors={showRequiredErrors}
        />
        <Panel
          header={intl.formatMessage({ id: 'labelConfiguracoesGerais' })}
          style={{ marginBottom: 0 }}
        >
          <ConfiguracaoSwitch
            defaultValue
            id="desconsiderar-colaboradores-inativos"
            title={intl.formatMessage(
              {
                id: 'labelDesconsiderarColaboradoresInativos',
              },
              { colaboradores: resources.colaboradores }
            )}
            tooltip={intl.formatMessage(
              {
                id: 'labelDesconsiderarColaboradoresInativosInfo',
              },
              { colaboradores: resources.colaboradores }
            )}
            model={models.desconsiderarColaboradoresInativos}
            disabled={disabled}
          />
          <ConfiguracaoSwitch
            id="permitir-apenas-uma-resposta-por-participante"
            title={intl.formatMessage({
              id: 'labelPermitirApenasUmaRespostaPorParticipante',
            })}
            description={intl.formatMessage({
              id: 'labelAposEnvioParticipanteNaoPoderaEnviarOutraSubmissao',
            })}
            model={models.permitirApenasUmaRespostaPorParticipante}
            disabled={disabled}
          />
          <ConfiguracaoSwitch
            id="randomizar-perguntas"
            title={intl.formatMessage({ id: 'labelRandomizarPerguntas' })}
            description={intl.formatMessage({
              id: 'msgRandomizarPerguntasDescricao',
            })}
            model={models.randomizarPerguntas}
            disabled={disabled}
          />
          <ConfiguracaoSwitch
            id="exibir-uma-pergunta-por-vez"
            title={intl.formatMessage({
              id: 'labelExibirUmaPerguntaDeCadaVez',
            })}
            description={intl.formatMessage({
              id: 'labelExibirUmaPerguntaDeCadaVezDescricao',
            })}
            model={formatoExibicaoPerguntaPorVezModel}
            disabled={disabled}
          />
          <ConfiguracaoSwitch
            id="exibir-barra-progresso"
            title={intl.formatMessage({
              id: 'labelExibirBarraProgresso',
            })}
            description={intl.formatMessage({
              id: 'labelExibirBarraProgressoPesquisaClimaDescricao',
            })}
            model={exibirBarraProgressoModel}
            disabled={disabled || !exibirUmaPerguntaPorVezAtivo}
          />
          {tipoIdentificacaoId ===
            TIPO_IDENTIFICACAO_FORMULARIO_GLOBAL.CONFIDENCIAL && (
            <>
              {/* Por enquanto oculto: opção de permitir remover confidencialidade da pesquisa de clima.
            <ConfiguracaoSwitch
              id="permitir-remover-confidencialidade"
              title={intl.formatMessage({
                id: 'labelPermitirRemoverConfidencialidade',
              })}
              description={intl.formatMessage({
                id: 'labelParticipantePoderaSeIdentificarVoluntariamenteDurantePesquisa',
              })}
              model={models.permitirRemoverConfidencialidade}
              disabled={disabled}
            /> */}
              <NumberField
                label={intl.formatMessage({ id: 'labelExibicaoResultados' })}
                description={intl.formatMessage(
                  {
                    id: 'labelResultadosAreaExibidosQuantidadeMinimaRespondentes',
                  },
                  { area: resources.area }
                )}
                model={models.quantidadeMinimaRespostasParaExibirResultado}
                disabled={disabled}
              />
            </>
          )}
        </Panel>
      </Panel>
    </>
  );
}

export default EnvioPesquisaClimaTab;
